import { useEffect } from "react";
import { Link } from "react-router-dom";
import Footer from "@/pages/home/components/Footer";
import { articles, ArticleData } from "@/mocks/articles";

interface ArticlePageProps {
  slug: string;
}

export default function ArticlePage({ slug }: ArticlePageProps) {
  const article: ArticleData | undefined = articles[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.subtitle,
    "datePublished": article.updatedDate,
    "dateModified": article.updatedDate,
    "author": {
      "@type": "Organization",
      "name": "VigorBuy Spreadsheet Hub",
      "url": "https://vigorbuy.forum"
    },
    "publisher": {
      "@type": "Organization",
      "name": "VigorBuy Spreadsheet Hub",
      "url": "https://vigorbuy.forum",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://vigorbuy.forum/guide/${article.slug}`
    },
    "about": article.category
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <span className="text-xl font-heading font-bold text-foreground-950 tracking-tight">
              VigorBuy
            </span>
          </Link>
          <div className="flex items-center gap-3 md:gap-4">
            <Link to="/" className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
              &larr; Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              vigorbuy sheet
            </a>
          </div>
        </div>
      </nav>

      <main className="pt-20 pb-12">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400 whitespace-nowrap">Updated {article.updatedDate}</span>
              <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime}</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.subtitle}
            </p>
          </header>

          <div className="space-y-6">
            {article.sections.map((section, idx) => (
              <section key={idx}>
                <h2 id={section.anchor} className="text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                  <a href={`#${section.anchor}`} className="no-underline text-foreground-950">{section.heading}</a>
                </h2>
                {section.paragraphs.map((para, pIdx) => (
                  <p key={pIdx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    {para}
                  </p>
                ))}
              </section>
            ))}
          </div>

          <div className="mt-12 p-6 md:p-8 bg-background-100 rounded-2xl border border-background-200/60 text-center">
            <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">
              Ready to Start Exploring?
            </h3>
            <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
              Access the latest Vigorbuy Spreadsheet and start discovering curated product finds across all 14 categories.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap text-center cursor-pointer"
              >
                Open Vigorbuy Spreadsheet
              </a>
              <Link
                to="/"
                className="w-full sm:w-auto px-8 py-3 rounded-full border-2 border-primary-200 text-primary-600 font-semibold text-sm hover:bg-primary-50 transition-colors whitespace-nowrap text-center cursor-pointer"
              >
                Back to Homepage
              </Link>
            </div>
          </div>

          <nav className="mt-8 pt-6 border-t border-background-200/60">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line text-sm w-4 h-4 flex items-center justify-center"></i>
              Return to VigorBuy Spreadsheet Hub
            </Link>
          </nav>
        </article>
      </main>

      <Footer />
    </div>
  );
}